import React, { useEffect, useState } from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import {
  useGetSupportContactReasonsQuery,
  useCreateSupportContactReasonMutation,
  useUpdateSupportContactReasonMutation,
  useReorderSupportContactReasonsMutation,
  useDeleteSupportContactReasonMutation,
} from "../../store/services/support";
import { SupportContactReason } from "../../models/support";

const getApiError = (e: any): string =>
  e?.data?.error?.message || "Ocurrió un error";

interface ReasonFormValues {
  label: string;
  email_subject_prefix: string;
}

const reasonValidationSchema = Yup.object().shape({
  label: Yup.string().trim().required("El label es requerido"),
  email_subject_prefix: Yup.string(),
});

export function SupportContactReasons() {
  const { data: reasons, isLoading, isFetching } = useGetSupportContactReasonsQuery();
  const [createReason, { isLoading: isCreating }] = useCreateSupportContactReasonMutation();
  const [updateReason, { isLoading: isUpdating }] = useUpdateSupportContactReasonMutation();
  const [reorderReasons, { isLoading: isReordering }] = useReorderSupportContactReasonsMutation();
  const [deleteReason, { isLoading: isDeleting }] = useDeleteSupportContactReasonMutation();

  const [orderedReasons, setOrderedReasons] = useState<SupportContactReason[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [reasonToEdit, setReasonToEdit] = useState<SupportContactReason | undefined>(undefined);
  const [reasonToDelete, setReasonToDelete] = useState<SupportContactReason | undefined>(undefined);
  const [togglingId, setTogglingId] = useState<string | undefined>(undefined);

  useEffect(() => {
    setOrderedReasons(Array.isArray(reasons) ? reasons : []);
  }, [reasons]);

  const isEdit = !!reasonToEdit;

  const formik = useFormik<ReasonFormValues>({
    initialValues: {
      label: reasonToEdit?.label || "",
      email_subject_prefix: reasonToEdit?.emailSubjectPrefix || "",
    },
    enableReinitialize: true,
    validationSchema: reasonValidationSchema,
    onSubmit: (values) => {
      const label = values.label.trim();
      const emailSubjectPrefix = values.email_subject_prefix.trim();

      if (reasonToEdit) {
        updateReason({
          id: reasonToEdit.id,
          label,
          email_subject_prefix: emailSubjectPrefix,
        })
          .unwrap()
          .then(() => {
            toast.success("Motivo actualizado");
            hideDialog();
          })
          .catch((e) => toast.error(getApiError(e)));
      } else {
        createReason({
          label,
          ...(emailSubjectPrefix ? { email_subject_prefix: emailSubjectPrefix } : {}),
        })
          .unwrap()
          .then(() => {
            toast.success("Motivo creado");
            hideDialog();
          })
          .catch((e) => toast.error(getApiError(e)));
      }
    },
  });

  const openNewDialog = () => {
    setReasonToEdit(undefined);
    setDialogVisible(true);
  };

  const openEditDialog = (reason: SupportContactReason) => {
    setReasonToEdit(reason);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setReasonToEdit(undefined);
    formik.resetForm();
  };

  const handleRowReorder = (e: DataTableRowReorderEvent<SupportContactReason[]>) => {
    const reordered = e.value;
    setOrderedReasons(reordered);
    reorderReasons({ reason_ids: reordered.map((r) => r.id) })
      .unwrap()
      .then(() => toast.success("Orden de motivos actualizado"))
      .catch((err) => {
        toast.error(getApiError(err));
        setOrderedReasons(Array.isArray(reasons) ? reasons : []);
      });
  };

  const handleToggleActive = (reason: SupportContactReason, isActive: boolean) => {
    setTogglingId(reason.id);
    updateReason({ id: reason.id, is_active: isActive })
      .unwrap()
      .then(() => {
        toast.success(isActive ? "Motivo activado" : "Motivo desactivado");
      })
      .catch((e) => toast.error(getApiError(e)))
      .finally(() => setTogglingId(undefined));
  };

  const handleDelete = () => {
    if (!reasonToDelete) return;
    deleteReason(reasonToDelete.id)
      .unwrap()
      .then(() => {
        toast.success("Motivo eliminado");
      })
      .catch((e) => toast.error(getApiError(e)))
      .finally(() => setReasonToDelete(undefined));
  };

  const activeBodyTemplate = (reason: SupportContactReason) => (
    <InputSwitch
      checked={reason.isActive}
      disabled={isUpdating && togglingId === reason.id}
      onChange={(e: InputSwitchChangeEvent) =>
        handleToggleActive(reason, !!e.value)
      }
    />
  );

  const actionsBodyTemplate = (reason: SupportContactReason) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-pencil"
        rounded
        outlined
        severity="info"
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
        onClick={() => openEditDialog(reason)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        rounded
        outlined
        severity="danger"
        tooltip="Eliminar"
        tooltipOptions={{ position: "top" }}
        onClick={() => setReasonToDelete(reason)}
      />
    </div>
  );

  const dialogFooter = (
    <div>
      <Button
        type="button"
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        onClick={hideDialog}
        disabled={isCreating || isUpdating}
      />
      <Button
        type="button"
        label="Guardar"
        icon="pi pi-check"
        loading={isCreating || isUpdating}
        onClick={() => formik.handleSubmit()}
      />
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
            <h2 className="m-0">Motivos de contacto</h2>
            <Button
              type="button"
              label="Nuevo motivo"
              icon="pi pi-plus"
              onClick={openNewDialog}
              loading={isCreating}
            />
          </div>

          <DataTable
            value={orderedReasons}
            loading={isLoading || isFetching || isReordering}
            reorderableRows
            onRowReorder={handleRowReorder}
            dataKey="id"
            responsiveLayout="scroll"
            emptyMessage="Todavía no hay motivos de contacto. Creá el primero con el botón «Nuevo motivo»."
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column field="label" header="Label" />
            <Column
              field="emailSubjectPrefix"
              header="Prefijo de asunto"
              body={(reason: SupportContactReason) =>
                reason.emailSubjectPrefix || "-"
              }
            />
            <Column
              field="isActive"
              header="Activo"
              body={activeBodyTemplate}
              style={{ width: "100px" }}
            />
            <Column
              header="Acciones"
              body={actionsBodyTemplate}
              style={{ width: "140px" }}
            />
          </DataTable>
        </div>
      </div>

      <Dialog
        header={isEdit ? "Editar motivo" : "Nuevo motivo"}
        visible={dialogVisible}
        style={{ width: "450px" }}
        modal
        onHide={hideDialog}
        footer={dialogFooter}
      >
        <form onSubmit={formik.handleSubmit} className="formgrid grid">
          <div className="field col-12">
            <label htmlFor="label" className="font-medium text-900">
              Label *
            </label>
            <InputText
              id="label"
              name="label"
              value={formik.values.label}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCreating || isUpdating}
              className={`w-full mt-2 ${
                formik.touched.label && formik.errors.label ? "p-invalid" : ""
              }`}
              placeholder="Ej: Problemas con mi cuenta"
              autoFocus
            />
            {formik.touched.label && formik.errors.label && (
              <small className="p-error block mt-1">{formik.errors.label}</small>
            )}
          </div>

          <div className="field col-12">
            <label htmlFor="email_subject_prefix" className="font-medium text-900">
              Prefijo de asunto
            </label>
            <InputText
              id="email_subject_prefix"
              name="email_subject_prefix"
              value={formik.values.email_subject_prefix}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCreating || isUpdating}
              className="w-full mt-2"
              placeholder="Ej: [Cuenta]"
            />
            <small className="text-600 block mt-1">
              Opcional. Se antepone al asunto del email que recibe soporte.
            </small>
          </div>
        </form>
      </Dialog>

      <Dialog
        header="Eliminar motivo"
        visible={!!reasonToDelete}
        style={{ width: "450px" }}
        modal
        onHide={() => setReasonToDelete(undefined)}
        footer={
          <div>
            <Button
              type="button"
              label="Cancelar"
              icon="pi pi-times"
              severity="secondary"
              onClick={() => setReasonToDelete(undefined)}
              disabled={isDeleting}
            />
            <Button
              type="button"
              label="Eliminar"
              icon="pi pi-trash"
              severity="danger"
              loading={isDeleting}
              onClick={handleDelete}
            />
          </div>
        }
      >
        {reasonToDelete && (
          <>
            <h3 className="text-red-500 mt-0">
              <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
            </h3>
            <p className="m-0">
              ¿Estás seguro que querés eliminar el motivo{" "}
              <b>{reasonToDelete.label}</b>? Esta acción no se puede deshacer.
            </p>
          </>
        )}
      </Dialog>
    </div>
  );
}
